import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Send, X, Loader, UtensilsCrossed } from 'lucide-react';
import { shared, buzz as buzzApi, auth } from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';

const HeartIcon = ({ filled, size = 16 }) => (
  <svg className={filled ? 'fill-red-500 text-red-500' : ''} width={size} height={size} fill={filled ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
  </svg>
);

const CommentIcon = ({ size = 16 }) => (
  <svg width={size} height={size} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

const formatTimeAgo = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const BuzzHistoryPage = () => {
  const { sourceRecipeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromRecipeId = location.state?.fromRecipeId;

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recipeTitle, setRecipeTitle] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [expandedComments, setExpandedComments] = useState({});
  const [commentText, setCommentText] = useState({});
  const [submittingComment, setSubmittingComment] = useState({});

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const resp = await auth.getCurrentUser();
        setIsLoggedIn(true);
        setCurrentUserId(resp.data?.user_id);
      } catch {
        setIsLoggedIn(false);
      }
    };
    checkAuth();
  }, []);

  useEffect(() => {
    fetchBuzzHistory();
  }, [sourceRecipeId]);

  const fetchBuzzHistory = async () => {
    try {
      const buzzResponse = await shared.getBuzzHistory(sourceRecipeId);
      setPosts(buzzResponse.data.buzz_posts || []);
      const recipeResponse = await shared.getRecipe(sourceRecipeId);
      setRecipeTitle(recipeResponse.data.title || '');
    } catch (error) {
      console.error('Failed to fetch buzz history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (buzzId) => {
    if (!isLoggedIn) return;
    try {
      await buzzApi.likePost(buzzId);
      setPosts(prev => prev.map(post => {
        if (post.buzz_id === buzzId) {
          const likes = post.likes || [];
          const alreadyLiked = likes.includes(currentUserId);
          return {
            ...post,
            likes: alreadyLiked ? likes.filter(id => id !== currentUserId) : [...likes, currentUserId],
            like_count: alreadyLiked ? (post.like_count || 1) - 1 : (post.like_count || 0) + 1
          };
        }
        return post;
      }));
    } catch (err) {
      console.error('Failed to like:', err);
    }
  };

  const handleAddComment = async (buzzId) => {
    if (!isLoggedIn) return;
    const text = commentText[buzzId]?.trim();
    if (!text) return;
    setSubmittingComment(prev => ({ ...prev, [buzzId]: true }));
    try {
      const response = await buzzApi.addComment(buzzId, text);
      const newComment = response.data.comment;
      setPosts(prev => prev.map(post => {
        if (post.buzz_id === buzzId) {
          return { ...post, comments: [...(post.comments || []), newComment] };
        }
        return post;
      }));
      setCommentText(prev => ({ ...prev, [buzzId]: '' }));
    } catch (error) {
      console.error('Failed to add comment:', error);
    } finally {
      setSubmittingComment(prev => ({ ...prev, [buzzId]: false }));
    }
  };

  const handleDeleteComment = async (buzzId, commentId) => {
    try {
      await buzzApi.deleteComment(buzzId, commentId);
      setPosts(prev => prev.map(post => {
        if (post.buzz_id === buzzId) {
          return { ...post, comments: (post.comments || []).filter(c => c.comment_id !== commentId) };
        }
        return post;
      }));
    } catch (error) {
      console.error('Failed to delete comment:', error);
    }
  };

  const goBack = () => {
    if (fromRecipeId) navigate(`/recipe/${fromRecipeId}`);
    else navigate(`/shared/${sourceRecipeId}`);
  };

  return (
    <div className="min-h-screen bg-white pb-20">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40 border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-3">
            <button onClick={goBack} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-testid="buzz-history-back">
              <ArrowLeft size={20} />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold text-gray-900">Buzz History</h1>
              {recipeTitle && <p className="text-sm text-gray-500 truncate">{recipeTitle}</p>}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {loading ? (
          <LoadingSpinner message="Loading buzz history..." />
        ) : posts.length === 0 ? (
          <EmptyState
            icon="🍯"
            title="No buzz yet!"
            description="Be the first to share your experience cooking this recipe!"
            action={<button onClick={goBack} className="inline-flex items-center gap-2 bg-amber-600 text-white px-6 py-3 rounded-full hover:bg-amber-700 transition-colors font-medium shadow-lg"><ArrowLeft size={20} />Back to Recipe</button>}
          />
        ) : (
          <div className="space-y-2">
            {posts.map((post) => {
              const comments = post.comments || [];
              const likeCount = post.like_count || post.likes?.length || 0;
              const commentCount = comments.length;
              const isLiked = currentUserId && (post.likes || []).includes(currentUserId);
              const isExpanded = expandedComments[post.buzz_id];
              const currentText = commentText[post.buzz_id] || '';
              const isSubmitting = submittingComment[post.buzz_id];
              const hives = post.hives || [];
              const showAllKey = `showAll_${post.buzz_id}`;

              return (
                <div key={post.buzz_id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-testid={`buzz-history-card-${post.buzz_id}`}>
                  {/* Hive destination */}
                  {hives.length > 0 && (
                    <div className="flex items-center gap-1.5 px-3 pt-2.5 pb-0">
                      {hives.map((h) => (
                        <button
                          key={h.hive_id}
                          onClick={() => navigate(`/hive/${h.hive_id}`)}
                          className="flex items-center gap-1 text-xs text-gray-500 hover:text-amber-600 transition-colors"
                          data-testid={`buzz-history-hive-link-${h.hive_id}`}
                        >
                          <span className="text-sm">{h.icon}</span>
                          <span className="font-medium">{h.name}</span>
                          {h.hive_type === 'private' && <span className="text-[10px]">🔒</span>}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Card body */}
                  <div className="flex p-3 gap-3">
                    {/* Thumbnail — always show, placeholder if no media */}
                    <div className="w-[76px] h-[76px] rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                      {post.has_media ? (
                        <BuzzMediaThumb buzzId={post.buzz_id} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <UtensilsCrossed size={24} className="text-gray-400" strokeWidth={1.5} />
                        </div>
                      )}
                    </div>

                    {/* Meta content */}
                    <div className="flex-1 min-w-0 flex flex-col justify-between">
                      <div>
                        {/* User + time */}
                        <div className="flex items-center gap-1.5">
                          {post.user_picture ? (
                            <img src={post.user_picture} alt="" className="w-5 h-5 rounded-full object-cover" />
                          ) : (
                            <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 font-bold text-[9px]">
                              {(post.user_name || 'U')[0].toUpperCase()}
                            </div>
                          )}
                          <span className="text-sm font-medium text-gray-800">{post.user_name || 'Honey User'}</span>
                          <span className="text-gray-300 text-[10px]">&middot;</span>
                          <span className="text-xs text-gray-400 flex-shrink-0">{formatTimeAgo(post.created_at)}</span>
                          {/* Star rating */}
                          {post.rating > 0 && (
                            <div className="flex items-center gap-0.5 ml-auto">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <span key={star} className={`text-xs ${star <= post.rating ? 'text-amber-400' : 'text-gray-200'}`}>★</span>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Caption */}
                        {post.caption && (
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">{post.caption}</p>
                        )}

                        {/* No hive indicator */}
                        {hives.length === 0 && (
                          <p className="text-xs text-gray-400 mt-0.5 italic">My Buzz only</p>
                        )}
                      </div>

                      {/* Actions row */}
                      <div className="flex items-center gap-3 pt-1.5">
                        {/* Like */}
                        <button
                          onClick={() => handleLike(post.buzz_id)}
                          className={`flex items-center gap-0.5 p-0.5 transition-colors ${isLiked ? 'text-red-500' : 'text-gray-400 hover:text-red-400'}`}
                          data-testid={`buzz-history-like-${post.buzz_id}`}
                        >
                          <HeartIcon filled={isLiked} size={16} />
                          {likeCount > 0 && <span className="text-[11px]">{likeCount}</span>}
                        </button>

                        {/* Comment toggle */}
                        <button
                          onClick={() => setExpandedComments(prev => ({ ...prev, [post.buzz_id]: !prev[post.buzz_id] }))}
                          className={`flex items-center gap-0.5 p-0.5 transition-colors ${isExpanded ? 'text-amber-600' : 'text-gray-400 hover:text-amber-400'}`}
                          data-testid={`buzz-history-comment-toggle-${post.buzz_id}`}
                        >
                          <CommentIcon size={16} />
                          {commentCount > 0 && <span className="text-[11px]">{commentCount}</span>}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Expandable comments */}
                  {isExpanded && (
                    <CommentsSection
                      comments={comments}
                      buzzId={post.buzz_id}
                      commentText={currentText}
                      setCommentText={(val) => setCommentText(prev => ({ ...prev, [post.buzz_id]: val }))}
                      isSubmitting={isSubmitting}
                      onAddComment={handleAddComment}
                      onDeleteComment={handleDeleteComment}
                      isLoggedIn={isLoggedIn}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

/** Lazy-load thumbnail for a buzz post */
const BuzzMediaThumb = ({ buzzId }) => {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch(`${window.location.origin}/api/buzz/${buzzId}/media`)
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(data => {
        if (!cancelled && data.media?.length > 0) {
          const m = data.media[0];
          setSrc(m.data?.startsWith('data:') ? m.data : `data:image/jpeg;base64,${m.data}`);
        }
        setLoading(false);
      })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [buzzId]);

  if (loading) return <div className="w-full h-full flex items-center justify-center"><Loader className="animate-spin text-amber-300" size={16} /></div>;
  if (src) return <img src={src} alt="" className="w-full h-full object-cover" />;
  return <div className="w-full h-full flex items-center justify-center"><UtensilsCrossed size={24} className="text-gray-400" strokeWidth={1.5} /></div>;
};

/** Inline comments section */
const CommentsSection = ({ comments, buzzId, commentText, setCommentText, isSubmitting, onAddComment, onDeleteComment, isLoggedIn }) => {
  const [showAll, setShowAll] = useState(false);
  const visibleComments = showAll ? comments : comments.slice(-3);

  return (
    <div className="border-t border-gray-100 bg-gray-50/80 px-3 py-2.5" data-testid={`buzz-history-comments-${buzzId}`}>
      {comments.length > 0 && (
        <div className="space-y-1.5 mb-2.5">
          {comments.length > 5 && !showAll && (
            <button onClick={() => setShowAll(true)} className="text-xs text-amber-600 hover:text-amber-700 font-medium">
              View all {comments.length} comments
            </button>
          )}
          {visibleComments.map((c) => (
            <div key={c.comment_id} className="flex items-start gap-1.5 group">
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-1 flex-wrap">
                  <span className="font-medium text-xs text-gray-800">{c.user_name}</span>
                  <span className="text-[10px] text-gray-400">&middot; {formatTimeAgo(c.created_at)}</span>
                </div>
                <p className="text-xs text-gray-600 leading-snug">{c.text}</p>
              </div>
              {onDeleteComment && (
                <button onClick={() => onDeleteComment(buzzId, c.comment_id)} className="p-0.5 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                  <X size={10} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {isLoggedIn && (
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onAddComment(buzzId); } }}
            placeholder="Add a comment..."
            className="flex-1 px-3 py-1.5 text-xs border border-gray-200 rounded-full bg-white focus:outline-none focus:ring-1 focus:ring-amber-400"
            disabled={isSubmitting}
            data-testid={`buzz-history-comment-input-${buzzId}`}
          />
          <button
            type="button"
            onPointerDown={(e) => { e.preventDefault(); if (commentText.trim()) onAddComment(buzzId); }}
            disabled={isSubmitting}
            className={`p-2 min-w-[36px] min-h-[36px] flex items-center justify-center rounded-full transition-colors ${
              commentText.trim() && !isSubmitting ? 'text-white bg-amber-600 hover:bg-amber-700' : 'text-gray-400 bg-gray-200'
            }`}
            style={{ touchAction: 'manipulation' }}
          >
            {isSubmitting ? <Loader size={14} className="animate-spin" /> : <Send size={14} />}
          </button>
        </div>
      )}
    </div>
  );
};

export default BuzzHistoryPage;
